import React, { forwardRef } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { Loader2, LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  iconOnly?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-gradient-to-r from-primary to-primary/80',
    'text-primary-foreground font-medium',
    'border border-primary/20',
    'hover:from-primary/90 hover:to-primary/70',
    'hover:shadow-[0_0_20px_rgba(0,217,255,0.3)]',
    'active:from-primary/80 active:to-primary/60',
    'disabled:from-primary/40 disabled:to-primary/30'
  ),
  secondary: cn(
    'bg-gradient-to-r from-secondary to-secondary/80',
    'text-secondary-foreground font-medium',
    'border border-secondary/20',
    'hover:from-secondary/90 hover:to-secondary/70',
    'hover:shadow-[0_0_20px_rgba(176,38,255,0.3)]',
    'active:from-secondary/80 active:to-secondary/60',
    'disabled:from-secondary/40 disabled:to-secondary/30'
  ),
  outline: cn(
    'bg-transparent',
    'text-foreground font-medium',
    'border border-border',
    'hover:bg-background-elevated hover:border-foreground-subtle',
    'active:bg-background-muted',
    'disabled:text-foreground-muted disabled:border-border'
  ),
  ghost: cn(
    'bg-transparent',
    'text-foreground font-medium',
    'border border-transparent',
    'hover:bg-background-elevated',
    'active:bg-background-muted',
    'disabled:text-foreground-muted'
  ),
  danger: cn(
    'bg-gradient-to-r from-destructive to-destructive/80',
    'text-white font-medium',
    'border border-destructive/20',
    'hover:from-destructive/90 hover:to-destructive/70',
    'hover:shadow-[0_0_20px_rgba(255,71,87,0.3)]',
    'active:from-destructive/80 active:to-destructive/60',
    'disabled:from-destructive/40 disabled:to-destructive/30'
  ),
  success: cn(
    'bg-gradient-to-r from-success to-success/80',
    'text-success-foreground font-medium',
    'border border-success/20',
    'hover:from-success/90 hover:to-success/70',
    'hover:shadow-[0_0_20px_rgba(46,213,115,0.3)]',
    'active:from-success/80 active:to-success/60',
    'disabled:from-success/40 disabled:to-success/30'
  ),
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs rounded-md gap-1.5',
  md: 'h-10 px-4 text-sm rounded-lg gap-2',
  lg: 'h-11 px-6 text-sm rounded-lg gap-2',
  xl: 'h-12 px-8 text-base rounded-lg gap-2.5',
};

const iconSizeMap: Record<ButtonSize, number> = {
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText,
      disabled = false,
      fullWidth = false,
      icon: Icon,
      iconPosition = 'left',
      iconOnly = false,
      className,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const iconSize = iconSizeMap[size];

    const content = (
      <>
        {/* Loading spinner or left icon */}
        {loading ? (
          <Loader2 className="animate-spin" size={iconSize} />
        ) : (
          Icon && iconPosition === 'left' && <Icon size={iconSize} />
        )}

        {/* Button text */}
        {!iconOnly && (
          <span>
            {loading && loadingText ? loadingText : children}
          </span>
        )}

        {/* Right icon */}
        {!loading && Icon && iconPosition === 'right' && !iconOnly && (
          <Icon size={iconSize} />
        )}

        {/* Icon only mode */}
        {iconOnly && !loading && Icon && <Icon size={iconSize} />}
      </>
    );

    return (
      <motion.button
        ref={ref}
        type={type}
        disabled={isDisabled}
        whileHover={!isDisabled ? { scale: 1.02 } : undefined}
        whileTap={!isDisabled ? { scale: 0.98 } : undefined}
        transition={{ duration: 0.1 }}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center',
          'transition-all duration-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          // Disabled state
          'disabled:cursor-not-allowed disabled:opacity-50',
          // Variant styles
          variantStyles[variant],
          // Size styles
          sizeStyles[size],
          // Full width
          fullWidth && 'w-full',
          // Icon only adjustments
          iconOnly && size === 'sm' && 'w-8 px-0',
          iconOnly && size === 'md' && 'w-10 px-0',
          iconOnly && size === 'lg' && 'w-11 px-0',
          iconOnly && size === 'xl' && 'w-12 px-0',
          // Custom class
          className
        )}
        {...props}
      >
        {content}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
